import React, { FC, useState, useRef, useEffect } from 'react';
import { Country } from '../../../types';

interface CountrySelectInputProps {
	width: number;
	title?: string;
	mandatory?: boolean;
	placeholder?: string;
	options: Country[];
	value: string;
	onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
	disabled?: boolean;
	wrong?: boolean;
	wrongMessage?: string;
	currentLanguage: string;
}

const CountrySelectInputDesktop: FC<CountrySelectInputProps> = ({
	width,
	title,
	mandatory,
	placeholder,
	options,
	value,
	onChange,
	disabled,
	wrong,
	wrongMessage,
	currentLanguage,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [hoveredOption, setHoveredOption] = useState<string | null>(null);
	const ref = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const isPlaceholderSelected = value === '';
	
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);
	
	const handleOptionClick = (optionValue: string) => {
		if (disabled) return;
		
		const event = {
			target: {
				value: optionValue,
			},
		} as React.ChangeEvent<HTMLSelectElement>;
		onChange(event);
		setIsOpen(false);
	};
	
	const selectedOption = options.find((option) => option.countryCode === value);
	
	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (!isOpen) return;
		
		const key = e.key;
		if (key.length === 1 && /[a-zA-Z0-9]/.test(key)) {
			const lowerKey = key.toLowerCase();
			const matchingOption =
				options.find(
					(option) => option.label[currentLanguage as keyof typeof option.label].toLowerCase().startsWith(lowerKey)
				) || null;
			
			if (matchingOption) {
				setHoveredOption(matchingOption.countryCode);
				setTimeout(() => {
					const optionElement = listRef.current?.querySelector(
						`[data-value="${matchingOption.countryCode}"]`
					);
					optionElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}, 0);
			}
		}
	};
	
	return (
		<div
			style={{ width: width, position: 'relative' }}
			ref={ref}
			tabIndex={0}
			onKeyDown={handleKeyDown}
		>
			{title && (
				<div
					style={{
						marginBottom: 4,
						fontWeight: '600',
						fontSize: 16,
						color: '#323E48',
					}}
				>
					{title} {mandatory && '*'}
				</div>
			)}
			<div
				style={{
					height: 48,
					background: disabled ? '#EDEDED' : '#FFFFFF',
					boxShadow: '0px 1px 6px #00000029',
					borderRadius: 10,
					border: wrong ? '2px solid red' : '1px solid #00000029',
					display: 'flex',
					alignItems: 'center',
					padding: '0 10px',
					boxSizing: 'border-box',
					cursor: disabled ? 'not-allowed' : 'pointer',
				}}
				onClick={() => !disabled && setIsOpen(!isOpen)}
			>
				<div
					style={{
						fontSize: 19,
						marginLeft: 4,
						color: isPlaceholderSelected ? '#c7cad9' : '#323E48',
						flex: 1,
					}}
				>
					{isPlaceholderSelected
						? placeholder || 'Sélectionnez une option'
						: selectedOption?.label[currentLanguage as keyof typeof selectedOption.label]}
				</div>
				<img
					src="/images/chevron-down.svg"
					alt="icon"
					style={{ marginLeft: 0, width: 15, height: 15 }}
				/>
			</div>
			{isOpen && !disabled && (
				<div
					ref={listRef}
					style={{
						position: 'absolute',
						top: 71,
						left: 0,
						width: '100%',
						background: '#FFFFFF',
						boxShadow: '0px 1px 6px #00000029',
						borderRadius: 10,
						zIndex: 1000,
						maxHeight: 350,
						overflowY: 'auto',
					}}
				>
					{options.map((option) => {
						const label = option.label[currentLanguage as keyof typeof option.label];
						return (
							<div
								key={option.countryCode}
								data-value={option.countryCode}
								onClick={() => handleOptionClick(option.countryCode)}
								onMouseEnter={() => setHoveredOption(option.countryCode)}
								onMouseLeave={() => setHoveredOption(null)}
								style={{
									padding: '12px 14px',
									cursor: 'pointer',
									backgroundColor:
										option.countryCode === value
											? '#EDEDED'
											: option.countryCode === hoveredOption
												? '#FF4C00'
												: 'transparent',
									color:
										option.countryCode === value
											? '#323E48'
											: option.countryCode === hoveredOption
												? '#FFFFFF'
												: '#323E48',
								}}
							>
								{label}
							</div>
						);
					})}
				</div>
			)}
			{wrong && (
				<div
					style={{
						position: 'absolute',
						bottom: -20,
						left: 5,
						fontSize: 12,
						color: 'red',
					}}
				>
					{wrongMessage}
				</div>
			)}
		</div>
	);
};

export default CountrySelectInputDesktop; 